import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { MapContainer, GeoJSON, TileLayer, Popup, Marker } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
// eslint-disable-next-line import/no-extraneous-dependencies
import 'leaflet-gpx'
import { Grid, Stack, Typography, Paper } from '@mui/material'
import { useAuth0 } from '@auth0/auth0-react'
import MarkerClusterGroup from 'react-leaflet-markercluster'
import {
  confirmedSite,
  unconfirmedSite,
  tasksAssigned,
  gpsWaypoint,
} from '../../../assets/leafletIcons'
import { sortHubUsersCurrentFirst } from '../../../utils/sortHubUsersCurrentFirst'
// eslint-disable-next-line import/no-extraneous-dependencies
import 'leaflet.markercluster/dist/MarkerCluster.css'
// eslint-disable-next-line import/no-extraneous-dependencies
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import ActivityAssignmentForm from './ActivityAssignmentForm'
import ActivityList from './ActivityList'
import { useHubUsers } from '../../../hooks/data/useHubUsers'
import { useUncroppedSamplingAreas } from '../../../hooks/data/useSamplingAreas'

const EventViewerMap = ({
  currentFarm,
  monitoringSitesWithTrackPoints,
  monitoringWorkflows,
  waypoints,
  setWaypoints,
  gpx,
  setMonitoringSites,
  eventData,
  setEventData,
  assignedTo,
}) => {
  const { user } = useAuth0()
  const [map, setMap] = useState()
  const { data: samplingAreas } = useUncroppedSamplingAreas({ farmId: currentFarm?.id })
  const { data: farmHubUsers } = useHubUsers(currentFarm?.hubId)
  const hubUsers = useMemo(
    () => sortHubUsersCurrentFirst(farmHubUsers ?? [], user?.email),
    [farmHubUsers, user?.email],
  )
  const markerRefs = useRef({})
  const setRef = (key, element) => {
    markerRefs.current[key] = element
  }

  const getMarkerIcon = useCallback(
    site => {
      const hastActivities = Object.keys(eventData)
        .map(key => eventData[key])
        .some(
          monitoringSite =>
            monitoringSite.id === site.id &&
            monitoringSite.monitoringActivities?.some(activity => activity.enabled),
        )
      if (hastActivities) {
        return tasksAssigned
      }
      if (site.locationConfirmed) {
        return confirmedSite
      }
      return unconfirmedSite
    },
    [eventData],
  )

  useEffect(() => {
    if (map && monitoringSitesWithTrackPoints && gpx) {
      const reader = new FileReader()

      // Convert the file blob into text
      reader.onload = e => {
        const gpxText = e.target.result

        // Create the GPX layer with marker options
        const gpxLayer = new L.GPX(gpxText, {
          gpx_options: {
            joinTrackSegments: false,
            parseElements: ['track'],
          },
          markers: {
            startIcon: null,
            endIcon: null,
            wptIcons: {
              '': gpsWaypoint.svg,
            },
          },
          async: true,
          polyline_options: {
            color: '#2196f3',
            weight: 2,
          },
        })

        // Add the GPX layer to the map
        gpxLayer.addTo(map)
      }
      reader.readAsText(gpx)
    }
  }, [gpx, monitoringSitesWithTrackPoints, map])

  return (
    <Grid container alignContent="flex-start" height="100%" spacing={2}>
      <Grid item xs={3}>
        <ActivityList eventData={eventData} markerRefs={markerRefs} setEventData={setEventData} />
      </Grid>
      <Grid item style={{ position: 'relative' }} xs={9}>
        <MapContainer
          ref={setMap}
          center={[51.505, -0.09]}
          style={{ height: '73vh', width: '100%' }}
          whenReady={({ target }) => {
            target.fitBounds(L.geoJSON(currentFarm.toGeoJSON).getBounds())
          }}
          zoom={13}
        >
          <TileLayer
            attribution=""
            subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
            url="https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
          />
          {currentFarm && (
            <GeoJSON
              data={currentFarm.toGeoJSON}
              style={{ fillColor: '#ff98200', color: '#ff9800', weight: 1 }}
            />
          )}
          {samplingAreas &&
            samplingAreas.length > 0 &&
            samplingAreas.map(sa => (
              <GeoJSON
                key={sa.id}
                data={sa.toGeoJSON}
                style={{ fillColor: sa.color || '#ff9800', color: '#ff9800', weight: 0.5 }}
              />
            ))}
          <MarkerClusterGroup>
            {waypoints &&
              waypoints.length > 0 &&
              waypoints.map(waypoint => (
                <Marker
                  key={waypoint.id}
                  icon={gpsWaypoint.leafletIcon}
                  position={[waypoint.lat, waypoint.lng]}
                >
                  <Popup>{waypoint.name}</Popup>
                </Marker>
              ))}
          </MarkerClusterGroup>

          {hubUsers.length > 0 &&
            monitoringSitesWithTrackPoints &&
            monitoringSitesWithTrackPoints.length > 0 &&
            monitoringSitesWithTrackPoints.map((monitoringSite, index) => (
              <Marker
                key={monitoringSite.id + monitoringSite.name}
                ref={element => setRef(monitoringSite.id, element)}
                autoPan
                autoPanOnFocus
                eventHandlers={{
                  click: ({ target }) => {
                    map.panTo(target.getLatLng(), { animate: true })
                  },
                }}
                icon={getMarkerIcon(monitoringSite).leafletIcon}
                offset={[20, 20]}
                position={[monitoringSite.location[0], monitoringSite.location[1]]}
              >
                <Popup autoPan keepInView autoPanPadding={[-20, -20]} style={{ width: '200px' }}>
                  <ActivityAssignmentForm
                    assignedTo={assignedTo}
                    currentFarm={currentFarm}
                    eventData={eventData}
                    hubUsers={hubUsers.map(hubUser => hubUser.email)}
                    markerRef={markerRefs.current[monitoringSite.id]}
                    monitoringSite={monitoringSite}
                    monitoringSiteIndex={index}
                    monitoringWorkflows={monitoringWorkflows}
                    setEventData={setEventData}
                    setMonitoringSites={setMonitoringSites}
                    setWaypoints={setWaypoints}
                    waypoints={waypoints}
                  />
                </Popup>
              </Marker>
            ))}
        </MapContainer>
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: '10px',
            borderRadius: '8px',
            zIndex: 1000,
            minWidth: 'max-content',
            maxWidth: '90%',
          }}
        >
          <Stack
            flex
            alignItems="center"
            direction={{ xs: 'column', sm: 'row' }}
            gap={2}
            justifyContent="center"
            width="100%"
          >
            <Stack flex alignItems="center" direction="row" gap={0.5} sx={{ whiteSpace: 'nowrap' }}>
              <img alt="confirmed site" src={confirmedSite.svg} width="22px" />
              <Typography sx={{ whiteSpace: 'nowrap' }} variant="caption">
                Sitio confirmado
              </Typography>
            </Stack>
            <Stack flex alignItems="center" direction="row" gap={0.5} sx={{ whiteSpace: 'nowrap' }}>
              <img alt="validation site" src={unconfirmedSite.svg} width="22px" />
              <Typography sx={{ whiteSpace: 'nowrap' }} variant="caption">
                Sitio sin confirmar
              </Typography>
            </Stack>
            <Stack flex alignItems="center" direction="row" gap={0.5} sx={{ whiteSpace: 'nowrap' }}>
              <img alt="pending site" src={gpsWaypoint.svg} width="22px" />
              <Typography sx={{ whiteSpace: 'nowrap' }} variant="caption">
                GPS Waypoint
              </Typography>
            </Stack>
            <Stack flex alignItems="center" direction="row" gap={0.5} sx={{ whiteSpace: 'nowrap' }}>
              <img alt="tasks assigned site" src={tasksAssigned.svg} width="22px" />
              <Typography sx={{ whiteSpace: 'nowrap' }} variant="caption">
                Sitio con tareas asignadas
              </Typography>
            </Stack>
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  )
}

export default EventViewerMap
